import Link from "next/link"
import { Separator } from "@radix-ui/react-separator"
import { observer } from "mobx-react-lite"
import { Address } from "viem"

import { Card } from "@/components/card"
import { ShowWhenTrue } from "@/components/conditionals"
import { ClFarmStore } from "@/domains/cl-farm/cl-farm-store"
import { ClFarmBreakdownStats } from "@/domains/cl-farms-page/components/cl-farms-breakdown-stats"
import { ClFarmsPageTabs } from "@/domains/cl-farms-page/components/cl-farms-page-tabs"
import { ClFarmAddresses } from "@/domains/cl-farms-page/components/clfarm-addresses"
import { UserPosition } from "@/domains/cl-farms-page/user-position"
import { MessageBox } from "@/domains/common/components/message-box"
import { CreateIcon } from "@/domains/icons"
import { cn, trimEthAddress } from "@/domains/library"
import useResponsive from "@/hooks/use-responsive"

export const ClFarmsPageLayout = observer(
  ({
    leftColumn,
    clFarmAddress,
    clFarm,
  }: {
    leftColumn: React.ReactNode
    clFarmAddress: Address | undefined
    clFarm: ClFarmStore | null | undefined
  }) => {
    const { isMobile, isTablet, isDesktop, width } = useResponsive()

    if (isDesktop && width > 1400) {
      return (
        <div className="text-t100 grid w-full grid-cols-1 gap-4 xl:grid-cols-[minmax(auto,560px)_1fr]">
          <div className="flex w-full min-w-[320px] max-w-xl flex-col gap-4">
            <ClFarmsPageTabs />
            <ShowWhenTrue when={clFarm === null}>
              <SearchingForPair className="col-start-1 row-start-2" />
            </ShowWhenTrue>
            <ShowWhenTrue when={clFarm === undefined}>
              <PairNotFound clFarmAddress={clFarmAddress!} className="col-start-1 row-start-2" />
            </ShowWhenTrue>
            <ShowWhenTrue when={!!clFarm}>{leftColumn}</ShowWhenTrue>
            <ClFarmAddresses clFarm={clFarm} className="col-start-1 row-start-5" />
          </div>
          <div className="flex max-w-xl flex-col gap-4">
            <ClFarmBreakdownStats
              clFarm={clFarm}
              className="col-start-1 row-start-4 lg:col-start-2 lg:row-span-2 lg:row-start-1"
            />
            <UserPosition clFarm={clFarm} className="col-start-1 row-start-3" />
          </div>
        </div>
      )
    }

    if (isDesktop) {
      return (
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-4">
            <ClFarmsPageTabs />
            <ShowWhenTrue when={clFarm === null}>
              <SearchingForPair className="col-start-1 row-start-2" />
            </ShowWhenTrue>
            <ShowWhenTrue when={clFarm === undefined}>
              <PairNotFound clFarmAddress={clFarmAddress!} className="col-start-1 row-start-2" />
            </ShowWhenTrue>
            <ShowWhenTrue when={!!clFarm}>{leftColumn}</ShowWhenTrue>
            <ClFarmAddresses clFarm={clFarm} className="col-start-1 row-start-5" />
          </div>
          <div className="flex flex-col gap-4">
            <ClFarmBreakdownStats
              clFarm={clFarm}
              className="col-start-1 row-start-4 lg:col-start-2 lg:row-span-2 lg:row-start-1"
            />
            <UserPosition clFarm={clFarm} className="col-start-1 row-start-3" />
          </div>
        </div>
      )
    }

    if (isMobile || isTablet) {
      return (
        <div className="text-t100 flex flex-col gap-4">
          <ClFarmsPageTabs />
          <ShowWhenTrue when={clFarm === null}>
            <SearchingForPair className="col-start-1 row-start-2" />
          </ShowWhenTrue>
          <ShowWhenTrue when={clFarm === undefined}>
            <PairNotFound clFarmAddress={clFarmAddress!} className="col-start-1 row-start-2" />
          </ShowWhenTrue>
          <ShowWhenTrue when={!!clFarm}>{leftColumn}</ShowWhenTrue>
          <UserPosition clFarm={clFarm} className="col-start-1 row-start-3" />
          <ClFarmBreakdownStats
            clFarm={clFarm}
            className="col-start-1 row-start-4 lg:col-start-2 lg:row-span-2 lg:row-start-1"
          />
          <ClFarmAddresses clFarm={clFarm} className="col-start-1 row-start-5" />
        </div>
      )
    }

    return (
      <div className={cn("grid", "text-t100 gap-4", "grid-cols-[minmax(auto,540px)_minmax(auto,540px)]")}>
        <div className="flex w-full min-w-[320px] max-w-xl flex-col gap-4">
          <ClFarmsPageTabs />
          <ShowWhenTrue when={clFarm === null}>
            <SearchingForPair className="col-start-1 row-start-2" />
          </ShowWhenTrue>
          <ShowWhenTrue when={clFarm === undefined}>
            <PairNotFound clFarmAddress={clFarmAddress!} className="col-start-1 row-start-2" />
          </ShowWhenTrue>
          <ShowWhenTrue when={!!clFarm}>{leftColumn}</ShowWhenTrue>
          <ClFarmAddresses clFarm={clFarm} className="col-start-1 row-start-5" />
        </div>

        <div className="flex max-w-xl flex-col gap-4">
          <ClFarmBreakdownStats
            clFarm={clFarm}
            className="col-start-1 row-start-4 lg:col-start-2 lg:row-span-2 lg:row-start-1"
          />
          <UserPosition clFarm={clFarm} className="col-start-1 row-start-3" />
        </div>
      </div>
    )
  },
)

// internal searching state
const PairNotFound = ({ clFarmAddress, className }: { clFarmAddress: Address; className?: string }) => {
  return (
    <Card.Container
      className={cn("bg-p400 border-p400-light flex w-full flex-col gap-6 rounded-lg border p-4 lg:p-6", className)}
    >
      <Card.Header>
        <Card.HeaderIconContainer>
          <CreateIcon size={24} className="text-primary-500" />
        </Card.HeaderIconContainer>

        <div className="flex w-full flex-col">
          <Card.HeaderTitle>Stake</Card.HeaderTitle>
          <Card.HeaderDescription>Stake for boosted rewards with liquidity automation.</Card.HeaderDescription>
        </div>
      </Card.Header>
      <Separator
        className="border-p400-light relative h-0 w-full overflow-visible rounded-md border border-solid
          after:pointer-events-none after:absolute after:inset-[-1px] after:z-[1] after:rounded-md 
          after:shadow-[inset_0.5px_0.5px_0.5px_0px_hsla(220,43%,5%,1)] after:content-['']"
        decorative
      />
      <MessageBox variant={"error"}>
        <p>
          Couldn't find pair with address {trimEthAddress(clFarmAddress ?? "")}, click{" "}
          <Link href={"/liquidity/cl-farms"} target="_self" className="underline">
            here
          </Link>{" "}
          to go back to CL Farms.
        </p>
      </MessageBox>
    </Card.Container>
  )
}

// internal searching state
const SearchingForPair = ({ className }: { className?: string }) => {
  return (
    <Card.Container
      className={cn("bg-p400 border-p400-light flex w-full flex-col gap-6 rounded-lg border p-4 lg:p-6", className)}
    >
      <Card.Header>
        <Card.HeaderIconContainer>
          <CreateIcon size={24} className="text-primary-500" />
        </Card.HeaderIconContainer>
        <div className="flex w-full flex-col">
          <Card.HeaderTitle>Stake</Card.HeaderTitle>
          <Card.HeaderDescription>Stake for boosted rewards with liquidity automation.</Card.HeaderDescription>
        </div>
      </Card.Header>
      <Separator
        className="border-p400-light relative h-0 w-full overflow-visible rounded-md border border-solid
          after:pointer-events-none after:absolute after:inset-[-1px] after:z-[1] after:rounded-md 
          after:shadow-[inset_0.5px_0.5px_0.5px_0px_hsla(220,43%,5%,1)] after:content-['']"
        decorative
      />
      <MessageBox variant={"info"}>Searching for pair data, one moment</MessageBox>
    </Card.Container>
  )
}
